"use client";

import React, { Suspense, useContext, useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { IconChevronRight } from "@/lib/icons";
import { generateBreadcrumbsFromPath } from "./breadcrumb.utils";
import styles from "./breadcrumb.module.css";
import BreadcrumbSchema from "./BreadcrumbSchema";
import Container from "@/components/container";
import { locationcontext } from "@/lib/context";

const cx = (...classes) => classes.filter(Boolean).join(" ");

function BreadcrumbsInner({
  items: customItems,
  customLabels = {},
  currentPageTitle,
  city: customCity,
  showHomeIcon = true,
  lastItemClickable = false,
  isPadding: customIsPadding,
  separator = <IconChevronRight size={14} />,
  baseURL = process.env.NEXT_PUBLIC_CUSTOMER_URL,
  hideOnMobile = false,
  // Show the current page's title in full (wrapping on narrow screens)
  // instead of ellipsizing it — used where the breadcrumb has the room.
  fullLastItem = false,
  style,
  className = "",
  // False when the page's CMS schema already declares a BreadcrumbList —
  // see app/(explore)/[[...slug]]/page.js.
  emitSchema = true,
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const is_application = searchParams.get("is_application");
  const { location } = useContext(locationcontext) || {};

  // Prefer the server-provided city. Cookie/context only after mount so SSR
  // and the first client paint use the same label (BUG-W2-06 hydration).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const isNoPaddingRoute = React.useMemo(() => {
    if (!pathname) return false;
    const targets = ["/profile", "/tickets", "/my-contests"];
    return targets.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`),
    );
  }, [pathname]);

  const isPadding = customIsPadding ?? !isNoPaddingRoute;

  const currentCity = React.useMemo(() => {
    if (customCity) return customCity;
    if (!mounted) return "ahmedabad";
    return location || "ahmedabad";
  }, [customCity, location, mounted]);

  // Determine breadcrumb items list
  const breadcrumbs = React.useMemo(() => {
    if (customItems && customItems.length > 0) {
      return customItems;
    }
    return generateBreadcrumbsFromPath(
      pathname,
      customLabels,
      currentPageTitle,
      currentCity,
    );
  }, [customItems, pathname, customLabels, currentPageTitle, currentCity]);

  if (is_application === "true") {
    return null;
  }

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <Container
      id="page-breadcrumb"
      className={cx(
        isPadding && styles.breadcrumbWrapper,
        !isPadding && "!px-0 !py-0 !mx-0 max-w-full",
        hideOnMobile && styles.hideOnMobile,
        className,
      )}
      style={style}
      suppressHydrationWarning
    >
      <nav
        aria-label="Breadcrumb"
        className={styles.breadcrumbNav}
        suppressHydrationWarning
      >
        <ol className={styles.breadcrumbList}>
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            const hasLink = crumb.href && (!isLast || lastItemClickable);

            return (
              <li
                key={`${crumb.title}-${index}`}
                className={styles.breadcrumbItem}
              >
                {index > 0 && (
                  <span className={styles.separator} aria-hidden="true">
                    {separator}
                  </span>
                )}
                {hasLink ? (
                  <Link href={crumb.href} className={styles.link} prefetch>
                    <span className={styles.truncation} suppressHydrationWarning>
                      {crumb.title}
                    </span>
                  </Link>
                ) : (
                  <span
                    className={isLast ? styles.activeItem : styles.link}
                    aria-current={isLast ? "page" : undefined}
                  >
                    <span
                      className={
                        isLast && fullLastItem
                          ? styles.fullTitle
                          : styles.truncation
                      }
                      suppressHydrationWarning
                    >
                      {crumb.title}
                    </span>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Render SEO structured markup */}
      {emitSchema && <BreadcrumbSchema items={breadcrumbs} baseURL={baseURL} />}
    </Container>
  );
}

export default function Breadcrumbs(props) {
  // useSearchParams requires a Suspense boundary in Next App Router
  return (
    <Suspense fallback={null}>
      <BreadcrumbsInner {...props} />
    </Suspense>
  );
}
